import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'

// ─── Datum-/tijd-helpers ──────────────────────────────────────────────────────

// Dag-precisie datums worden over de API uitgewisseld als 'yyyy-mm-dd' strings.
// In de DB staan ze als UTC-midnight van de NL-kalenderdag (codebase-conventie,
// zie schema.prisma OperationeleTaak + spec 34 § 2). Timestamps blijven ISO.
const DAG_DATUM_RE = /^\d{4}-\d{2}-\d{2}$/
const dagDatum = (label = 'datum') =>
  z.string().regex(DAG_DATUM_RE, { message: `${label} moet een datum zijn (yyyy-mm-dd)` })

// Tijd-van/-tot op de taak zijn "HH:mm" (24-uurs, NL-tijd).
const TIJD_RE = /^([01]\d|2[0-3]):[0-5]\d$/
const tijdHHmm = (label = 'tijd') =>
  z.string().regex(TIJD_RE, { message: `${label} moet een tijd zijn (HH:mm)` })

// Geocode-coördinaten: het form stuurt getallen (uit een Places-/Maps-selectie),
// de route converteert naar Prisma-Decimal(9,6). WGS84-grenzen; finite afgedwongen
// zodat NaN/Infinity nooit doorgegeven worden.
const coordLat = z
  .number()
  .finite()
  .min(-90, { message: 'Breedtegraad moet tussen -90 en 90 liggen' })
  .max(90, { message: 'Breedtegraad moet tussen -90 en 90 liggen' })
const coordLng = z
  .number()
  .finite()
  .min(-180, { message: 'Lengtegraad moet tussen -180 en 180 liggen' })
  .max(180, { message: 'Lengtegraad moet tussen -180 en 180 liggen' })

// ─── Enum-tuples (matchen de Prisma-enums als string-union) ───────────────────

export const TAAK_TYPE = [
  'VOERTUIG_OPHALEN',
  'VOERTUIG_BRENGEN',
  'GEREEDSCHAP_BRENGEN',
  'GEREEDSCHAP_OPHALEN',
  'MATERIAAL_BRENGEN',
  'MATERIAAL_OPHALEN',
  'AUTOSLEUTEL_OPHALEN',
  'PBM_KLEDING_AFLEVEREN',
  'DOCUMENT_OPHALEN',
  'DOCUMENT_LATEN_TEKENEN',
  'INTERNE_TAAK',
  'SPOEDACTIE_KLANT',
  'OVERIG',
] as const
export type TaakType = (typeof TAAK_TYPE)[number]

export const TAAK_PRIORITEIT_OP = ['HOOG', 'MIDDEL', 'LAAG'] as const
export type TaakPrioriteitOp = (typeof TAAK_PRIORITEIT_OP)[number]

export const OPERATIONELE_TAAK_STATUS = [
  'BACKLOG',
  'CONTROLE_NODIG',
  'GEPLAND',
  'UITGEVOERD',
  'NIET_UITGEVOERD',
  'GEANNULEERD',
  // batch-61 taken-filters-status (FO-447) — ADD-VALUE; beide met verplichte
  // reden. Spec-mapping: OPEN = BACKLOG, GEREED = UITGEVOERD (zie de labels).
  'ON_HOLD',
  'HERPLANNEN',
] as const
export type OperationeleTaakStatus = (typeof OPERATIONELE_TAAK_STATUS)[number]

export const TAAK_BEWIJS_EIS = ['GEEN', 'OPMERKING', 'FOTO', 'HANDTEKENING', 'KILOMETERSTAND'] as const
export type TaakBewijsEis = (typeof TAAK_BEWIJS_EIS)[number]

export const NIET_UITGEVOERD_REDEN = [
  'GEEN_TIJD',
  'KLANT_NIET_AANWEZIG',
  'MATERIAAL_NIET_AANWEZIG',
  'VOERTUIG_NIET_AANWEZIG',
  'ADRES_ONJUIST',
  'NIET_HAALBAAR_ROUTE',
  'AUTOMATISCH_EINDE_DAG',
  'ANDERS',
] as const
export type NietUitgevoerdReden = (typeof NIET_UITGEVOERD_REDEN)[number]

export const TAAK_AUTO_REGEL_KEY = [
  'UITDIENST_INNAME',
  'VOERTUIGWISSEL',
  'DEVICE_AFLEVEREN',
  'CONTRACT_TEKENEN',
  'NIEUWE_KLANT_PBM',
  // batch-57 overeenkomst-vervolg (FO-415 §3.5) — bruikleenovereenkomst opstellen
  // na een berijderwissel of een telefoon-/gereedschap-uitgifte. Eén taak per
  // uitgifte-moment; de voorvulling (medewerker, middelen, variant) staat op de
  // bijbehorende `BruikleenTrigger`.
  'BRUIKLEEN_OVEREENKOMST',
  // batch-67 campagnes (FO-501 §B5) - opvolgtaken na een goedgekeurde
  // bijtellingskeuze (bronEntiteitId = CampagneDeelname.id).
  'BIJTELLING_NAW',
  'BIJTELLING_VERKLARING',
  'BIJTELLING_WISSEL',
] as const
export type TaakAutoRegelKey = (typeof TAAK_AUTO_REGEL_KEY)[number]

/**
 * `bronModule` van de magazijnlevering-taak: een goedgekeurde uitrusting-
 * bestelling (kleding, PBM of gereedschap uit de catalogus) of
 * gereedschapsaanvraag die uit het magazijn geleverd kan worden. Zie
 * apps/api/src/services/magazijn-levering-taak.ts.
 */
export const MAGAZIJN_LEVERING_BRON_MODULE = 'magazijn-levering'
/** `bronSubKey` van de magazijnlevering-taak: wat voor bron het is. */
export const MAGAZIJN_LEVERING_BRON = ['UITRUSTING_BESTELLING', 'GEREEDSCHAP_AANVRAAG'] as const
export type MagazijnLeveringBron = (typeof MAGAZIJN_LEVERING_BRON)[number]

export const TAAK_IMPORT_STATUS = ['GEUPLOAD', 'GECONTROLEERD', 'VERWERKT', 'MISLUKT'] as const
export type TaakImportStatus = (typeof TAAK_IMPORT_STATUS)[number]

export const TAAK_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'GEIMPORTEERD',
  'PRIORITEIT_GEWIJZIGD',
  'GEPLAND',
  'TOEGEWEZEN',
  'UITGEVOERD',
  'NIET_UITGEVOERD',
  'TERUGGEZET',
  'GEESCALEERD',
  'OPMERKING',
  'FOTO',
  'AUTO_CONTEXT_OPGEHAALD',
  'VOORRAAD_AFGEBOEKT',
  'AUTO_AANGEMAAKT',
  // batch-45 onboarding (FO-381) — taak gesloten door de onboarding-opruiming.
  'GEANNULEERD',
  // batch-61 taken-filters-status (FO-447) — statuswissel via de matrix
  // (on hold / herplannen / hervatten / terug naar open), met oude/nieuwe
  // status + reden op de audit-rij.
  'STATUS_GEWIJZIGD',
] as const
export type TaakAuditActie = (typeof TAAK_AUDIT_ACTIE)[number]

/**
 * Slimme taaktypes die auto-context ophalen (spec 34 § 6). Voor deze types is
 * `betreftMedewerkerId` verplicht (Zod-superRefine + template-flag).
 */
export const SLIMME_TAAK_TYPES = [
  'VOERTUIG_OPHALEN',
  'VOERTUIG_BRENGEN',
  'GEREEDSCHAP_BRENGEN',
  'GEREEDSCHAP_OPHALEN',
  'MATERIAAL_BRENGEN',
  'MATERIAAL_OPHALEN',
  'AUTOSLEUTEL_OPHALEN',
  'PBM_KLEDING_AFLEVEREN',
] as const satisfies ReadonlyArray<TaakType>
const SLIMME_TAAK_TYPES_SET = new Set<TaakType>(SLIMME_TAAK_TYPES)

/**
 * batch-62 taken-ux (FO-464) — inline veldfout op `betreftMedewerkerId` als een
 * slim taaktype of een sjabloon met `betreftMedewerkerVerplicht` de medewerker
 * om wie het gaat mist. Eén tekst, gedeeld door Zod, de API en het formulier.
 */
export const BETREFT_MEDEWERKER_VERPLICHT_MELDING =
  'Kies de medewerker om wie het gaat. Dat is verplicht bij deze soort taak.'

// ─── Label-records (NL-talig voor UI, geen em-dash als verbindingsteken) ───────

export const TAAK_TYPE_LABELS: Record<TaakType, string> = {
  VOERTUIG_OPHALEN:      'Voertuig ophalen',
  VOERTUIG_BRENGEN:      'Voertuig brengen',
  GEREEDSCHAP_BRENGEN:   'Gereedschap brengen',
  GEREEDSCHAP_OPHALEN:   'Gereedschap ophalen',
  MATERIAAL_BRENGEN:     'Materiaal brengen',
  MATERIAAL_OPHALEN:     'Materiaal ophalen',
  AUTOSLEUTEL_OPHALEN:   'Autosleutel ophalen',
  PBM_KLEDING_AFLEVEREN: 'PBM / kleding afleveren',
  DOCUMENT_OPHALEN:      'Document ophalen',
  DOCUMENT_LATEN_TEKENEN: 'Document laten tekenen',
  INTERNE_TAAK:          'Interne taak',
  SPOEDACTIE_KLANT:      'Spoedactie klant',
  OVERIG:                'Overig',
}

export const TAAK_PRIORITEIT_OP_LABELS: Record<TaakPrioriteitOp, string> = {
  HOOG:   'Hoog',
  MIDDEL: 'Middel',
  LAAG:   'Laag',
}

// UI-labels volgen de spec-woorden van FO-447 (Open / Gepland / On hold /
// Herplannen / Gereed); de enum-keys zelf blijven (events + crons hangen eraan).
export const OPERATIONELE_TAAK_STATUS_LABELS: Record<OperationeleTaakStatus, string> = {
  BACKLOG:         'Open',
  CONTROLE_NODIG:  'Controle nodig',
  GEPLAND:         'Gepland',
  ON_HOLD:         'On hold',
  HERPLANNEN:      'Herplannen',
  UITGEVOERD:      'Gereed',
  NIET_UITGEVOERD: 'Niet uitgevoerd',
  GEANNULEERD:     'Geannuleerd',
}

export const TAAK_BEWIJS_EIS_LABELS: Record<TaakBewijsEis, string> = {
  GEEN:          'Geen',
  OPMERKING:     'Opmerking',
  FOTO:          'Foto',
  HANDTEKENING:  'Handtekening',
  KILOMETERSTAND: 'Kilometerstand',
}

export const NIET_UITGEVOERD_REDEN_LABELS: Record<NietUitgevoerdReden, string> = {
  GEEN_TIJD:              'Geen tijd',
  KLANT_NIET_AANWEZIG:    'Klant niet aanwezig',
  MATERIAAL_NIET_AANWEZIG: 'Materiaal niet aanwezig',
  VOERTUIG_NIET_AANWEZIG: 'Voertuig niet aanwezig',
  ADRES_ONJUIST:          'Adres onjuist',
  NIET_HAALBAAR_ROUTE:    'Niet haalbaar binnen route',
  AUTOMATISCH_EINDE_DAG:  'Automatisch (einde dag)',
  ANDERS:                 'Anders',
}

export const TAAK_AUTO_REGEL_KEY_LABELS: Record<TaakAutoRegelKey, string> = {
  // batch-71 (FO-535 §C12): vervallen; de inname loopt via de offboarding en de innamelijst.
  UITDIENST_INNAME: 'Uitdienst: spullen innemen (vervallen)',
  VOERTUIGWISSEL:   'Voertuigwissel',
  DEVICE_AFLEVEREN: 'Device afleveren',
  CONTRACT_TEKENEN: 'Contract laten tekenen',
  NIEUWE_KLANT_PBM: 'Nieuwe klant: PBM controleren',
  BRUIKLEEN_OVEREENKOMST: 'Bruikleenovereenkomst opstellen',
  BIJTELLING_NAW:        'Bijtelling: correctie naar salarisverwerker',
  BIJTELLING_VERKLARING: 'Bijtelling: verklaring indienen',
  BIJTELLING_WISSEL:     'Bijtelling: verklaring na autowissel',
}

/**
 * batch-67 middelen-inname fix1 (B3) - auto-regels die NIET met de schakelaar
 * onder Taken > Instellingen aan of uit gaan, maar op de plek waar de hele flow
 * wordt ingesteld. Voor BRUIKLEEN_OVEREENKOMST is de taak het vangnet als de
 * overeenkomst niet automatisch kan worden opgesteld; een losse schakelaar
 * ernaast zou dat vangnet stil kunnen uitzetten terwijl Middelen en bruikleen
 * zegt dat er dan een taak komt. Het beheerscherm toont hier een verwijzing en de
 * API weigert de schakelaar (422 AUTO_REGEL_ELDERS_INGESTELD).
 */
export const TAAK_AUTO_REGEL_ELDERS_INGESTELD: Partial<
  Record<TaakAutoRegelKey, { plek: string; href: string; uitleg: string }>
> = {
  BRUIKLEEN_OVEREENKOMST: {
    plek: 'Instellingen > Middelen en bruikleen',
    href: '/instellingen/middelen',
    uitleg:
      'Deze taak is het vangnet als de overeenkomst niet automatisch kan worden opgesteld, en staat daarom altijd aan. ' +
      'Of de overeenkomst automatisch wordt opgesteld, stel je per soort in onder Middelen en bruikleen.',
  },
}

// Badge-variant per status voor consistent kleurgebruik in cards/tabel.
export const OPERATIONELE_TAAK_STATUS_BADGE: Record<
  OperationeleTaakStatus,
  'secondary' | 'success' | 'warning' | 'danger' | 'info'
> = {
  BACKLOG:         'secondary',
  CONTROLE_NODIG:  'warning',
  GEPLAND:         'info',
  ON_HOLD:         'secondary',
  HERPLANNEN:      'warning',
  UITGEVOERD:      'success',
  NIET_UITGEVOERD: 'danger',
  GEANNULEERD:     'secondary',
}

// batch-62 taken-ux (FO-464 §2.C.11) — uitleg per status in gewone taal, voor de
// tooltip op elke statusbadge ("geen jargon-status zonder uitleg").
export const OPERATIONELE_TAAK_STATUS_UITLEG: Record<OperationeleTaakStatus, string> = {
  BACKLOG:         'Open: nog niet ingepland. De taak kan al wel aan iemand zijn toegewezen.',
  CONTROLE_NODIG:  'Controle nodig: bij het importeren ontbraken gegevens. Vul ze aan voordat de taak wordt ingepland.',
  GEPLAND:         'Gepland: de taak heeft een medewerker en een datum.',
  ON_HOLD:         'On hold: de taak wacht ergens op (zie de reden). Medewerker en datum blijven staan.',
  HERPLANNEN:      'Herplannen: de taak moet opnieuw ingepland worden (zie de reden).',
  UITGEVOERD:      'Gereed: de taak is gereedgemeld.',
  NIET_UITGEVOERD: 'Niet uitgevoerd: de taak is afgemeld en moet opnieuw ingepland worden.',
  GEANNULEERD:     'Geannuleerd: de taak hoeft niet meer te gebeuren.',
}

/**
 * batch-62 taken-ux (FO-464 §2.B.8) — hoe lang een gereedmelding met één klik
 * ongedaan te maken is ("toch niet gereed"). Daarna alleen via Status wijzigen
 * (Heropenen) met een reden.
 */
export const GEREEDMELDING_ONGEDAAN_VENSTER_UUR = 24

// ─── DTOs ─────────────────────────────────────────────────────────────────────

/**
 * Werktijden/werkdagen van de betreft-medewerker in de context-snapshot
 * (spec portal-werktijden Deel 1). Afgeleid uit bestaande Employee-velden
 * (`availability`, `werkUrenPerWeek`/`werkdagenPerWeek`-cascade en het vaste
 * weekrooster `urenMaandag..urenZondag`) — geen nieuw rooster-model. Alleen
 * werkdagen/werktijden, nooit gevoelige velden.
 */
export interface TaakContextWerktijden {
  /** Compacte leesbare samenvatting, bv. "ma, di, do, vr · 38 u/week". */
  samenvatting: string
  /** Werkdagen kort ("ma, di, do, vr") of null als onbekend. */
  werkdagen: string | null
  /** Uren per week (werkUrenPerWeek ?? contract+ATV-cascade) of null. */
  urenPerWeek: number | null
  /** Bij een vast weekrooster: uren per dag (alleen dagen > 0), anders null. */
  rooster: Array<{ dag: string; uren: number }> | null
}

/**
 * Read-only auto-context-snapshot (spec 34 § 6). Afgeleid op plan-moment uit de
 * actieve voertuigtoewijzing + de planning (`WerkbonDayEntry`) van de
 * betreft-medewerker; herberekend bij wijziging van datum/betreft-medewerker.
 */
export interface TaakContextSnapshot {
  voertuig: {
    id: string
    kenteken: string
    merk: string
    model: string
  } | null
  planning: {
    clientId: string
    clientNaam: string
    locatieAdres: string
    plannedStart: string
    plannedEnd: string
    contact: { naam: string; telefoon: string } | null
  } | null
  /**
   * Werktijden van de betreft-medewerker, of null als daar niets over bekend
   * is. Optioneel: snapshots van vóór deze uitbreiding missen het veld —
   * de UI degradeert dan door de regel weg te laten.
   */
  werktijden?: TaakContextWerktijden | null
  /** Gezet als er geen planning voor die dag gevonden is (niets verzinnen). */
  melding?: string
}

export interface OperationeleTaakDTO {
  id: string
  type: TaakType
  templateId: string | null
  titel: string
  omschrijving: string | null
  prioriteit: TaakPrioriteitOp
  status: OperationeleTaakStatus
  volgorde: number

  // Planning
  uitvoerderId: string | null
  /** 'yyyy-mm-dd' of null. */
  geplandeDatum: string | null
  tijdVan: string | null
  tijdTot: string | null
  /** 'yyyy-mm-dd' of null. */
  gewensteDatum: string | null
  /** 'yyyy-mm-dd' of null. */
  deadline: string | null

  // Categorie (visuele tag) naast `type` (systeem-semantiek)
  categorie: string | null
  geschatteDuurMin: number | null

  // Koppelingen
  clientId: string | null
  locatieAdres: string | null
  locatiePlaceId: string | null
  /** Decimal(9,6) als string (Prisma-Decimal) of null — geocode van locatieAdres. */
  locatieLat: string | null
  locatieLng: string | null
  werkbonId: string | null
  voertuigId: string | null
  voertuigVrij: string | null
  uitrustingItemId: string | null
  voorraadArtikelId: string | null
  voorraadUniekItemId: string | null
  documentId: string | null
  betreftMedewerkerId: string | null

  // Auto-context (read-only snapshot)
  contextSnapshot: TaakContextSnapshot | null

  // Opvolging / escalatie
  aantalKeerGepland: number
  aantalKeerNietUitgevoerd: number
  escalatieBasisPrioriteit: TaakPrioriteitOp
  escalatieFails: number
  /** 'yyyy-mm-dd' of null. */
  laatstGeplandeDatum: string | null
  /**
   * De vorige uitvoerder van een afgemelde (NIET_UITGEVOERD) taak — bewaard
   * wanneer `uitvoerderId` op null gaat. Vormt samen met `laatstGeplandeDatum`
   * de "effectieve" uitvoerder/datum waarop planbord/stats/lijst een afgemelde
   * taak blijven plaatsen. null voor taken die nooit afgemeld zijn.
   */
  laatstUitvoerderId: string | null
  nietUitgevoerdReden: string | null
  nietUitgevoerdOpmerking: string | null
  /** ISO-timestamp of null. */
  geescaleerdOp: string | null
  /** ISO-timestamp of null. */
  signaalVerstuurdOp: string | null
  /** ISO-timestamp of null. */
  herinneringVerstuurdOp: string | null
  // batch-67 taken-engine (FO-498 §2.5) — markers van de deadline-bewaking. Een
  // marker betekent "voor een deadline afgehandeld", NIET "gemeld": hij wordt ook
  // gezet zonder melding (bij het uitrollen al verstreken, buiten het venster,
  // aangemaakt na de deadline) en kan bij een eerdere deadline horen. Toon ze
  // dus niet als "verstuurd"; gebruik daarvoor `deadlineMeldingen` op het detail.
  /** ISO-timestamp van de laatste herinnering-afhandeling (interne marker), of null. */
  deadlineHerinneringOp: string | null
  /** ISO-timestamp van de laatste escalatie-afhandeling (interne marker), of null. */
  deadlineEscalatieOp: string | null

  // batch-61 taken-filters-status (FO-447) — reden bij ON_HOLD/HERPLANNEN.
  /** Vrije reden (verplicht bij ON_HOLD/HERPLANNEN), gewist zodra de taak die status verlaat. */
  statusReden: string | null
  /** ISO-timestamp waarop de reden is gezet, of null. */
  statusRedenOp: string | null
  /** ISO-timestamp van de laatste statuswissel via de matrix, of null. */
  statusGewijzigdOp: string | null

  // Uitvoering / bewijs
  /** ISO-timestamp of null. */
  uitgevoerdOp: string | null
  uitgevoerdDoorId: string | null
  uitvoerOpmerking: string | null
  kilometerstand: number | null
  /** Decimal(9,6) als string (Prisma-Decimal) of null. */
  uitvoerLat: string | null
  uitvoerLng: string | null
  handtekeningStorageKey: string | null
  /** ISO-timestamp of null. */
  voorraadAfgeboektOp: string | null

  bewijsEis: TaakBewijsEis
  /** Komt deze taak bij de uitvoerder als popup omhoog in het portaal (tot hij is afgehandeld)? */
  toonAlsPopup: boolean
  importBatchId: string | null

  // Auto-aanmaken / herkomst
  autoRegelKey: TaakAutoRegelKey | null
  bronModule: string | null
  /**
   * batch-71 workflow-sjablonen (FO-533 §A5) - een taak van een stappenplan. Zolang er geen
   * uitvoerder is staat hij bij deze doelgroepen ("Ik pak dit op"). `workflowDeeplink` is
   * het scherm waar het moet gebeuren (`portal:` voor de portal).
   */
  doelgroepSleutels: string[]
  workflowDeeplink: string | null
  bronEntiteitId: string | null
  /**
   * Is dit de ene taak van een offboarding ("Offboarding <naam>")? Dan opent de
   * lijst direct de actiepunten. Bewust een afgeleide vlag en niet de ruwe
   * `bronSubKey`: die verraadt bij afgeschermde taken (verhaal, bezwaar) wat er speelt.
   */
  isOffboardingHoofdtaak?: boolean
  /** batch-71 (FO-535 §C12): de taak "Inname <naam>" hoort bij deze innamelijst. */
  innameSessieId?: string
  /** batch-71 kennismaking (FO-534 §B6): de taak opent de nalooplijst. Afgeleide vlag. */
  isKennismakingTaak?: boolean
  /**
   * Bij een magazijnlevering-taak (`bronModule` = MAGAZIJN_LEVERING_BRON_MODULE):
   * waar de bron staat, zodat het detail kan doorklikken. Afgeleid uit de
   * `bronSubKey`, alleen voor deze bron.
   */
  magazijnLeveringBron?: MagazijnLeveringBron

  /** 'yyyy-mm-dd' of null — DagTaak-provenance. */
  origineleDatum: string | null
  createdById: string

  createdAt: string
  updatedAt: string
  deletedAt: string | null

  // Server-side geresolved (display-velden, niet in DB)
  voertuigKenteken?: string | null
  clientNaam?: string | null
  uitvoerderNaam?: string | null
  betreftMedewerkerNaam?: string | null

  // batch-62 taken-ux (FO-464) — afgeleide velden voor de kaart/rij en de
  // gereedmeld-knop. Optioneel: niet elke bron levert ze.
  /** Werkbonnummer van de gekoppelde werkbon (koppeling op de kaart), of null. */
  werkbonNummer?: string | null
  /** Aantal nog niet afgevinkte checklist-punten (bevestigingsvraag bij gereedmelden). */
  openChecklistItems?: number
  /**
   * Mag DEZE gebruiker de gereedmelding nog met één klik ongedaan maken
   * (binnen 24 uur, als melder of als beheer)? Alleen gezet door endpoints die
   * de gebruiker kennen (Mijn taken, detail).
   */
  magGereedmeldingOngedaan?: boolean
  /** batch-71 workflow-sjablonen: staat bij mijn doelgroep en is nog van niemand ("Ik pak dit op"). */
  magOppakken?: boolean
  /** Naam van wie de taak gereedmeldde (detail-endpoints), of null. */
  uitgevoerdDoorNaam?: string | null
}

export interface TaakTemplateDTO {
  id: string
  naam: string
  type: TaakType
  standaardTitel: string | null
  standaardPrioriteit: TaakPrioriteitOp
  bewijsEis: TaakBewijsEis
  verplichteVelden: string[] | null
  checklistTemplate: string[] | null
  autoContext: boolean
  betreftMedewerkerVerplicht: boolean
  actief: boolean
  /**
   * batch-62 taken-ux (FO-464) — standaard-deadline in kalenderdagen na
   * aanmaken; null = het sjabloon zet geen deadline.
   */
  standaardDeadlineDagen: number | null
  createdAt: string
  updatedAt: string
}

export interface TaakChecklistItemDTO {
  id: string
  taakId: string
  volgorde: number
  label: string
  gedaan: boolean
  /** ISO-timestamp of null. */
  gedaanOp: string | null
}

export interface TaakFotoDTO {
  id: string
  taakId: string
  storageKey: string
  fileName: string
  createdAt: string
  // batch-67 taken-engine (FO-498 §2.2) — bijlagen zijn foto's of documenten.
  /** MIME-type, of null voor een foto van voor batch-67 (altijd een afbeelding). */
  mimeType: string | null
  /** Bestandsgrootte in bytes, of null (oude rij). */
  fileSize: number | null
  /** Bewijs bij het gereedmelden (telt voor de bewijs-eis FOTO) of een bijlage bij de taak. */
  soort: TaakFotoSoort
}

/**
 * Eén foto in de portal-detail-DTO. `url` wijst naar het own-data-serveer-endpoint
 * (`GET /operationele-taken/mijn/:id/fotos/:fotoId`) — NIET de rauwe `storageKey`,
 * zodat het filesysteem-pad niet lekt en de monteur alleen z'n eigen foto's haalt.
 */
export interface TaakDetailFotoDTO {
  id: string
  url: string
  fileName: string
  /** batch-67: MIME-type (null = afbeelding van voor batch-67). */
  mimeType: string | null
  /** batch-67: bewijs bij het gereedmelden of een bijlage bij de taak. */
  soort: TaakFotoSoort
}

// ─── batch-67 taken-engine (FO-498 §2.2): bijlagen bij aanmaken ──────────────

/**
 * Wat een rij in `TaakFoto` is (review B7). Alleen BEWIJS telt voor de
 * bewijs-eis FOTO: een foto die de planner bij het aanmaken meegeeft is geen
 * bewijs van de uitvoerder. Rijen van voor batch-67 zijn allemaal BEWIJS.
 */
export const TAAK_FOTO_SOORT = ['BEWIJS', 'BIJLAGE'] as const
export type TaakFotoSoort = (typeof TAAK_FOTO_SOORT)[number]

/** Maximum aantal bijlagen per upload (aanmaken of toevoegen); de multipart-laag staat 5 bestanden toe. */
export const TAAK_BIJLAGE_MAX = 5

/**
 * Maximum van alle bijlagen in een upload SAMEN (review B4). De server-actions
 * van backoffice en portal accepteren 25 MB per verzoek (`bodySizeLimit`); met
 * 20 MB blijft er ruimte voor de multipart-omhulling en de taakgegevens. De
 * browser verkleint foto's eerst, dus in de praktijk passen vijf foto's ruim.
 */
export const TAAK_BIJLAGEN_TOTAAL_MAX_BYTES = 20 * 1024 * 1024
export const TAAK_BIJLAGEN_TOTAAL_MAX_MB = TAAK_BIJLAGEN_TOTAAL_MAX_BYTES / (1024 * 1024)

/**
 * Toegestane bijlage-types: de bewijsfoto-types plus pdf. Bewust geen Office-
 * formaten: een taak-bijlage is een foto van de situatie of een (werk)document
 * dat de uitvoerder op zijn telefoon moet kunnen openen.
 */
export const TAAK_BIJLAGE_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
])

/** `accept`-attribuut voor de bijlagen-input. */
export const TAAK_BIJLAGE_ACCEPT = [...TAAK_BIJLAGE_MIME_TYPES].join(',')

/** Veldnaam van het JSON-deel in `POST /operationele-taken/met-bijlagen` (multipart). */
export const TAAK_MET_BIJLAGEN_VELD = 'taak'

/** Is deze bijlage een afbeelding (thumbnail) of een document (bestandslink)? Oude rijen (null) zijn foto's. */
export function isTaakBijlageAfbeelding(mimeType: string | null | undefined): boolean {
  return mimeType == null || mimeType.startsWith('image/')
}

/**
 * Eén compacte historie-regel voor de portal-taakdetail (afgeleid uit `TaakAudit`).
 * Bevat alleen de voor de monteur relevante gebeurtenissen (gepland / uitgevoerd /
 * niet-uitgevoerd) met de actor-naam server-side geresolved (Map id -> name??email)
 * en, bij niet-uitgevoerd, de opgegeven reden.
 */
export interface TaakHistorieRegel {
  actie: TaakAuditActie
  /** ISO-timestamp. */
  op: string
  actorNaam: string | null
  /** Reden bij NIET_UITGEVOERD (enum-key) of bij STATUS_GEWIJZIGD (vrije tekst), anders null. */
  reden: string | null
  /** batch-61 — de nieuwe status bij STATUS_GEWIJZIGD/GEPLAND/TERUGGEZET, anders null. */
  nieuweStatus?: OperationeleTaakStatus | null
}

/**
 * Verrijkte detail-DTO voor de portal-eigen-taakdetail
 * (`GET /operationele-taken/mijn/:id`). Bovenop de basis-`OperationeleTaakDTO`
 * (incl. `contextSnapshot`) levert hij: checklist-items, foto's (als veilige
 * URL's), geresolveerde display-namen (klant/voertuig/document/betreft-medewerker),
 * een werkbon-link en een compacte historie. Bewust GEEN interne/gevoelige velden
 * (taken hebben geen kostprijs/marge; we voegen niets toe wat dat zou lekken).
 */
export interface OperationeleTaakDetailDTO extends OperationeleTaakDTO {
  checklistItems: TaakChecklistItemDTO[]
  fotos: TaakDetailFotoDTO[]
  /** Geresolveerde klantnaam (ook in basis-DTO als `clientNaam`; hier verplicht). */
  klantNaam: string | null
  /** Gekoppelde werkbon (voor een doorklik-link), of null. */
  werkbonId: string | null
  werkbonNummer: string | null
  /** Voertuig: gestructureerd indien gekoppeld, anders de vrije-tekst-fallback. */
  voertuigKenteken: string | null
  voertuigMerk: string | null
  voertuigModel: string | null
  voertuigVrij: string | null
  /** Naam van het gekoppelde te-tekenen/ophalen document, of null. */
  documentNaam: string | null
  /** Naam van de betreft-medewerker (van wie auto/locatie/PBM is), of null. */
  betreftMedewerkerNaam: string | null
  bewijsEis: TaakBewijsEis
  /** Compacte historie (gepland / uitgevoerd / niet-uitgevoerd), nieuwste eerst. */
  historie: TaakHistorieRegel[]
}

/** Status van één geanalyseerde import-cel: gematcht, ambigu of ontbrekend. */
export type TaakImportSuggestieStatus = 'ok' | 'ambigu' | 'ontbreekt'

export interface TaakImportSuggestie {
  veld: string
  ruw: string
  suggestie: { id: string; label: string; score: number } | null
  status: TaakImportSuggestieStatus
}

export interface TaakImportRijDTO {
  /** Ruwe kolomwaarden uit de geüploade rij (kolomnaam -> waarde). */
  ruw: Record<string, string>
  suggesties: TaakImportSuggestie[]
}

export interface TaakAuditDTO {
  id: string
  taakId: string
  actie: TaakAuditActie
  /** Raw userId (codebase-conventie); naam apart geresolved. */
  actor: string
  actorNaam?: string | null
  oudeWaarde: unknown
  nieuweWaarde: unknown
  reden: string | null
  createdAt: string
}

/** Periode-scope voor de per-uitvoerder-doorsnede in de stats (default 'week'). */
export const OPERATIONELE_TAAK_STATS_PERIODE = ['vandaag', 'week'] as const
export type OperationeleTaakStatsPeriode = (typeof OPERATIONELE_TAAK_STATS_PERIODE)[number]

/**
 * Eén uitvoerder-rij in de stats: de planning-doorsnede over de gekozen periode
 * (`periode` query-param, Europe/Amsterdam). Invariant: `gepland = uitgevoerd +
 * nietUitgevoerd + open`. Geteld op `geplandeDatum` binnen [van, tot]:
 *   - gepland        = alle taken gepland in de periode (GEPLAND + UITGEVOERD +
 *                      NIET_UITGEVOERD met geplandeDatum in de periode)
 *   - uitgevoerd      = daarvan UITGEVOERD
 *   - nietUitgevoerd  = daarvan NIET_UITGEVOERD
 *   - open            = daarvan nog GEPLAND (niet afgerond)
 * `naam` is server-side geresolved (Map id -> name??email).
 */
export interface OperationeleTaakPerUitvoerder {
  uitvoerderId: string
  naam: string
  gepland: number
  uitgevoerd: number
  nietUitgevoerd: number
  open: number
}

/**
 * Dashboard-aggregaties voor `GET /operationele-taken/stats` (spec 34 § 11).
 * Eén tel per tegel + groeperingen per uitvoerder en per taaktype.
 *
 * De top-level tegels (open/hogePrio/vandaagGepland/...) zijn periode-onafhankelijk
 * (huidige momentopname). `perUitvoerder` is de planning-doorsnede over de gekozen
 * `periode` (vandaag of de hele week ma-zo); `periode` echoot de gebruikte scope.
 *
 * NB: bewust `OperationeleTaakStatsDTO` (niet `TaakStatsDTO`) — die naam is al
 * bezet door de aggregator-`Taak`-module (`./taken`); de gedeelde barrel
 * (`packages/types/src/index.ts`) zou anders dubbel exporteren.
 */
export interface OperationeleTaakStatsDTO {
  /** Welke periode-scope `perUitvoerder` gebruikt ('vandaag' of 'week'). */
  periode: OperationeleTaakStatsPeriode
  /** Open lijst: BACKLOG + CONTROLE_NODIG + NIET_UITGEVOERD. */
  open: number
  /** Open taken met prioriteit HOOG. */
  hogePrio: number
  /** GEPLAND met geplandeDatum = vandaag. */
  vandaagGepland: number
  /** Status NIET_UITGEVOERD. */
  nietUitgevoerd: number
  /** aantalKeerNietUitgevoerd >= 2 (en niet UITGEVOERD/GEANNULEERD). */
  tweeKeerNietUitgevoerd: number
  /** Open/gepland met deadline < vandaag. */
  verlopenDeadlines: number
  /** Planning-doorsnede per uitvoerder over de gekozen periode (zie type-doc). */
  perUitvoerder: OperationeleTaakPerUitvoerder[]
  /** Open + geplande taken per taaktype (momentopname, periode-onafhankelijk). */
  perType: Array<{ type: TaakType; aantal: number }>
}

/** Query-schema voor `GET /operationele-taken/stats` — periode-scope (default week). */
export const operationeleTaakStatsQuerySchema = z.object({
  periode: z.enum(OPERATIONELE_TAAK_STATS_PERIODE).optional().default('week'),
})
export type OperationeleTaakStatsQuery = z.infer<typeof operationeleTaakStatsQuerySchema>

// ─── Zod-schemas (alle ID-refs via idParam()) ─────────────────────────────────

const bewijsEisSchema = z.enum(TAAK_BEWIJS_EIS)
const prioriteitOpSchema = z.enum(TAAK_PRIORITEIT_OP)
const taakTypeSchema = z.enum(TAAK_TYPE)

/**
 * POST /operationele-taken — losse taak aanmaken. Type-/template-afhankelijke
 * verplichte velden worden server-side afgedwongen; slimme taaktypes (§ 6)
 * vereisen `betreftMedewerkerId` (zie superRefine onderaan).
 */
export const createOperationeleTaakSchema = z
  .object({
    type:                taakTypeSchema.default('OVERIG'),
    titel:               z.string().trim().min(1).max(200),
    omschrijving:        z.string().trim().max(5000).nullable().optional(),
    prioriteit:          prioriteitOpSchema.optional(),
    categorie:           z.string().trim().max(100).nullable().optional(),
    geschatteDuurMin:    z.number().int().min(0).max(1440).nullable().optional(),
    clientId:            idParam('clientId').nullable().optional(),
    locatieAdres:        z.string().trim().max(500).nullable().optional(),
    locatiePlaceId:      z.string().trim().max(300).nullable().optional(),
    locatieLat:          coordLat.nullable().optional(),
    locatieLng:          coordLng.nullable().optional(),
    werkbonId:           idParam('werkbonId').nullable().optional(),
    voertuigId:          idParam('voertuigId').nullable().optional(),
    voertuigVrij:        z.string().trim().max(100).nullable().optional(),
    uitrustingItemId:    idParam('uitrustingItemId').nullable().optional(),
    voorraadArtikelId:   idParam('voorraadArtikelId').nullable().optional(),
    voorraadUniekItemId: idParam('voorraadUniekItemId').nullable().optional(),
    documentId:          idParam('documentId').nullable().optional(),
    betreftMedewerkerId: idParam('betreftMedewerkerId').nullable().optional(),
    gewensteDatum:       dagDatum('gewensteDatum').nullable().optional(),
    deadline:            dagDatum('deadline').nullable().optional(),
    tijdVan:             tijdHHmm('tijdVan').nullable().optional(),
    tijdTot:             tijdHHmm('tijdTot').nullable().optional(),
    bewijsEis:           bewijsEisSchema.optional(),
    toonAlsPopup:        z.boolean().optional(),
    templateId:          idParam('templateId').nullable().optional(),
    volgorde:            z.number().int().min(0).optional(),
    // batch-61 taken-filters-status (FO-447) — toewijzen bij aanmaken. Eén
    // uitvoerder (`uitvoerderId`) of meerdere (`uitvoerderIds`: per medewerker
    // een eigen taak). Met `geplandeDatum` wordt de taak direct GEPLAND (zelfde
    // pad als /plannen: audit + TAAK_TOEGEWEZEN); zonder datum blijft hij Open
    // maar toegewezen (zichtbaar in Mijn taken onder "Nog geen datum").
    uitvoerderId:        idParam('uitvoerderId').nullable().optional(),
    uitvoerderIds:       z.array(idParam('uitvoerderId')).max(25).optional(),
    geplandeDatum:       dagDatum('geplandeDatum').nullable().optional(),
    // batch-62 taken-ux (FO-464 §2.A.1) — checklist onder "meer opties". Is hij
    // meegestuurd, dan is dit de checklist (ook als die leeg is: de gebruiker
    // heeft de sjabloon-punten weggehaald); anders geldt die van het sjabloon.
    checklistItems:      z.array(z.string().trim().min(1).max(200)).max(50).optional(),
  })
  .superRefine((data, ctx) => {
    // Slimme taaktypes (§ 6) vereisen een betreft-medewerker: de auto-context
    // (voertuig/locatie/PBM) wordt van die persoon afgeleid.
    if (SLIMME_TAAK_TYPES_SET.has(data.type) && !data.betreftMedewerkerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['betreftMedewerkerId'],
        message: BETREFT_MEDEWERKER_VERPLICHT_MELDING,
      })
    }
    // Een geplande datum zonder medewerker is geen planning (GEPLAND vereist beide).
    const heeftUitvoerder = !!data.uitvoerderId || (data.uitvoerderIds?.length ?? 0) > 0
    if (data.geplandeDatum && !heeftUitvoerder) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['uitvoerderId'],
        message: 'Kies een medewerker om de taak direct in te plannen',
      })
    }
  })
export type CreateOperationeleTaakInput = z.infer<typeof createOperationeleTaakSchema>

/** Alle uitvoerders uit een create-payload (enkelvoud + meervoud, gededupliceerd). */
export function uitvoerdersUitCreateInput(input: {
  uitvoerderId?: string | null
  uitvoerderIds?: string[]
}): string[] {
  const ids = [...(input.uitvoerderIds ?? [])]
  if (input.uitvoerderId) ids.unshift(input.uitvoerderId)
  return [...new Set(ids)]
}

/**
 * PATCH /operationele-taken/:id — alle velden optioneel (partial), inclusief
 * status. Geen superRefine: een bestaande taak kan stapsgewijs aangevuld worden.
 */
export const updateOperationeleTaakSchema = z
  .object({
    type:                taakTypeSchema.optional(),
    titel:               z.string().trim().min(1).max(200).optional(),
    omschrijving:        z.string().trim().max(5000).nullable().optional(),
    prioriteit:          prioriteitOpSchema.optional(),
    status:              z.enum(OPERATIONELE_TAAK_STATUS).optional(),
    categorie:           z.string().trim().max(100).nullable().optional(),
    geschatteDuurMin:    z.number().int().min(0).max(1440).nullable().optional(),
    clientId:            idParam('clientId').nullable().optional(),
    locatieAdres:        z.string().trim().max(500).nullable().optional(),
    locatiePlaceId:      z.string().trim().max(300).nullable().optional(),
    locatieLat:          coordLat.nullable().optional(),
    locatieLng:          coordLng.nullable().optional(),
    werkbonId:           idParam('werkbonId').nullable().optional(),
    voertuigId:          idParam('voertuigId').nullable().optional(),
    voertuigVrij:        z.string().trim().max(100).nullable().optional(),
    uitrustingItemId:    idParam('uitrustingItemId').nullable().optional(),
    voorraadArtikelId:   idParam('voorraadArtikelId').nullable().optional(),
    voorraadUniekItemId: idParam('voorraadUniekItemId').nullable().optional(),
    documentId:          idParam('documentId').nullable().optional(),
    betreftMedewerkerId: idParam('betreftMedewerkerId').nullable().optional(),
    gewensteDatum:       dagDatum('gewensteDatum').nullable().optional(),
    deadline:            dagDatum('deadline').nullable().optional(),
    tijdVan:             tijdHHmm('tijdVan').nullable().optional(),
    tijdTot:             tijdHHmm('tijdTot').nullable().optional(),
    bewijsEis:           bewijsEisSchema.optional(),
    toonAlsPopup:        z.boolean().optional(),
    templateId:          idParam('templateId').nullable().optional(),
    volgorde:            z.number().int().min(0).optional(),
  })
  .superRefine((data, ctx) => {
    // batch-61: een statuswissel met verplichte reden (ON_HOLD/HERPLANNEN) loopt
    // via POST /:id/status; de generieke PATCH accepteert die statussen niet.
    if (data.status === 'ON_HOLD' || data.status === 'HERPLANNEN') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['status'],
        message: 'Deze status vereist een reden; gebruik de statusactie',
      })
    }
  })
export type UpdateOperationeleTaakInput = z.infer<typeof updateOperationeleTaakSchema>

// ─── batch-61 taken-filters-status (FO-447): statuswissel + toewijzen ─────────

/** Minimale redenlengte bij ON_HOLD/HERPLANNEN; de matrix in @streep/utils herexporteert deze. */
export const TAAK_STATUS_REDEN_MIN = 3
const NIET_UITGEVOERD_REDEN_SET = new Set<string>(NIET_UITGEVOERD_REDEN)

/**
 * POST /operationele-taken/:id/status — snelle statuswissel via de matrix.
 * - ON_HOLD / HERPLANNEN: `reden` verplicht (vrije tekst, server-side afgedwongen).
 * - UITGEVOERD: optionele `notitie` (uitvoer-opmerking); bewijs-eis blijft gelden
 *   voor de uitvoerder (portal), beheer sluit administratief af.
 * - NIET_UITGEVOERD: `reden` moet een afmeldreden-enum zijn (bestaand escalatiepad).
 * - GEPLAND: alleen "hervatten" vanuit ON_HOLD (datum + uitvoerder staan er al);
 *   inplannen vanuit een open status loopt via /plannen.
 */
export const taakStatusWijzigSchema = z
  .object({
    status:  z.enum(OPERATIONELE_TAAK_STATUS),
    reden:   z.string().trim().max(2000).nullable().optional(),
    notitie: z.string().trim().max(5000).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const reden = data.reden?.trim() ?? ''
    if ((data.status === 'ON_HOLD' || data.status === 'HERPLANNEN') && reden.length < TAAK_STATUS_REDEN_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: data.status === 'ON_HOLD' ? 'Reden is verplicht bij On hold' : 'Reden is verplicht bij Herplannen',
      })
    }
    if (data.status === 'NIET_UITGEVOERD' && !NIET_UITGEVOERD_REDEN_SET.has(reden)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Kies een afmeldreden',
      })
    }
  })
export type TaakStatusWijzigInput = z.infer<typeof taakStatusWijzigSchema>

/**
 * POST /operationele-taken/:id/toewijzen — (her)toewijzen aan een medewerker.
 * Met `geplandeDatum` wordt de taak GEPLAND via het plan-pad; zonder datum
 * blijft de status staan en wisselt alleen de uitvoerder (audit TOEGEWEZEN +
 * TAAK_TOEGEWEZEN naar de nieuwe uitvoerder). Alleen een taak die al GEPLAND is
 * houdt zonder expliciete datum zijn datum (batch-67). Een tijd hoort bij een
 * plandatum: een gewijzigde tijd zonder datum geeft een veldfout op
 * `geplandeDatum` (de route toetst dat, omdat een geplande taak zijn datum houdt).
 */
export const toewijzenTaakSchema = z.object({
  uitvoerderId:  idParam('uitvoerderId'),
  geplandeDatum: dagDatum('geplandeDatum').nullable().optional(),
  tijdVan:       tijdHHmm('tijdVan').nullable().optional(),
  tijdTot:       tijdHHmm('tijdTot').nullable().optional(),
})
export type ToewijzenTaakInput = z.infer<typeof toewijzenTaakSchema>

/** Filter-opties voor de backoffice-lijst (bron-modules die daadwerkelijk voorkomen). */
export interface TaakFilterOptiesDTO {
  bronModules: string[]
}

// ─── batch-67 taken-engine (FO-498 §2.5): bulk-acties + "mijn team" ──────────

/** Maximum aantal taken per bulk-actie (de UI selecteert per pagina). */
export const TAAK_BULK_MAX = 100

const taakIdsSchema = z
  .array(idParam('taakId'))
  .min(1, { message: 'Kies minimaal een taak.' })
  .max(TAAK_BULK_MAX, { message: `Maximaal ${TAAK_BULK_MAX} taken per keer.` })

/**
 * POST /operationele-taken/bulk/toewijzen — dezelfde medewerker op meerdere
 * taken. Met `geplandeDatum` wordt elke taak ingepland (GEPLAND). ZONDER datum
 * verandert de status niet: een geplande taak houdt zijn datum, bij elke andere
 * status (Open, On hold, Herplannen) wisselt alleen de uitvoerder. Een tijdveld
 * dat ontbreekt blijft ongewijzigd, `null` wist het; een tijd invullen kan
 * alleen samen met een datum. Per taak een eigen uitkomst: een afgesloten of
 * ontoegankelijke taak staat in `mislukt`, de rest gaat door.
 */
export const bulkToewijzenTakenSchema = z
  .object({
    taakIds:       taakIdsSchema,
    uitvoerderId:  idParam('uitvoerderId'),
    geplandeDatum: dagDatum('geplandeDatum').nullable().optional(),
    tijdVan:       tijdHHmm('tijdVan').nullable().optional(),
    tijdTot:       tijdHHmm('tijdTot').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    // Een tijd zonder datum zou bij een niet-geplande taak stil genegeerd worden.
    if (!v.geplandeDatum && (v.tijdVan != null || v.tijdTot != null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geplandeDatum'],
        message: 'Kies een datum als je een tijd invult.',
      })
    }
  })
export type BulkToewijzenTakenInput = z.infer<typeof bulkToewijzenTakenSchema>

/**
 * POST /operationele-taken/bulk/herplannen — meerdere taken op een nieuwe
 * datum. Zonder `uitvoerderId` houdt elke taak zijn huidige (of laatste)
 * uitvoerder; een taak zonder uitvoerder komt in `mislukt` ("kies eerst een
 * medewerker"). Een tijdveld dat ontbreekt blijft ongewijzigd, `null` wist het.
 */
export const bulkHerplannenTakenSchema = z.object({
  taakIds:       taakIdsSchema,
  geplandeDatum: dagDatum('geplandeDatum'),
  uitvoerderId:  idParam('uitvoerderId').nullable().optional(),
  tijdVan:       tijdHHmm('tijdVan').nullable().optional(),
  tijdTot:       tijdHHmm('tijdTot').nullable().optional(),
})
export type BulkHerplannenTakenInput = z.infer<typeof bulkHerplannenTakenSchema>

/** Uitkomst van een bulk-actie: wat lukte en wat niet (met reden per taak). */
export interface TaakBulkResultaatDTO {
  gelukt: number
  mislukt: Array<{ taakId: string; titel: string; melding: string }>
  /** Niet-blokkerende route-waarschuwingen van het plan-pad. */
  waarschuwingen: string[]
}

/**
 * `meta` naast de lijst van `GET /operationele-taken`. `mijnTeam` is alleen
 * gevuld bij het filter `mijnTeam=true`: hoeveel teamleden de server vond (0 =
 * de lijst is leeg omdat er geen team is, niet omdat er geen taken zijn).
 */
export interface OperationeleTakenListMetaDTO {
  total: number
  page: number
  pageSize: number
  mijnTeam?: { leden: number }
}

// ─── batch-67 taken-engine (FO-498 §2.3/§2.5): portal ───────────────────────

/** GET /operationele-taken/mijn/open-ids — ids van ALLE eigen open taken (ongeacht datum). */
export interface MijnTakenOpenIdsDTO {
  ids: string[]
  /**
   * Er waren er meer dan het plafond. De meldingenlijst toont "Gereedmelden"
   * dan bij elke taak-melding en laat de taak zelf beslissen bij het openen.
   */
  afgekapt: boolean
}

/** GET /operationele-taken/mijn/volgende — meta naast de eerstvolgende eigen taak (of null). */
export interface VolgendeTaakMetaDTO {
  /** Aantal eigen open taken dat na deze nog wacht (0 = dit was de laatste). */
  resterend: number
}

// ─── batch-67 taken-engine (FO-498 §2.6): auto-regel-logboek ─────────────────

export const TAAK_AUTO_REGEL_LOG_UITKOMST = [
  'AANGEMAAKT',
  'OVERGESLAGEN_UIT',
  'OVERGESLAGEN_DEDUPE',
  'OVERGESLAGEN_GEEN_SET',
  'FOUT',
] as const
export type TaakAutoRegelLogUitkomst = (typeof TAAK_AUTO_REGEL_LOG_UITKOMST)[number]

export const TAAK_AUTO_REGEL_LOG_UITKOMST_LABELS: Record<TaakAutoRegelLogUitkomst, string> = {
  AANGEMAAKT:            'Taken aangemaakt',
  OVERGESLAGEN_UIT:      'Overgeslagen: regel staat uit',
  OVERGESLAGEN_DEDUPE:   'Overgeslagen: bestond al voor deze bron',
  OVERGESLAGEN_GEEN_SET: 'Overgeslagen: deze regel maakt hier geen taak',
  FOUT:                  'Mislukt',
}

/** Een deadline-melding die echt is verstuurd: wanneer en aan wie. */
export interface TaakDeadlineMeldingDTO {
  /** ISO-timestamp van de (laatste) melding. */
  op: string
  /**
   * De ontvanger(s) bij naam ("Jan Jansen", of "Jan Jansen, Piet Peters en 2
   * anderen"), of null als die niet meer te herleiden zijn.
   */
  aan: string | null
}

/**
 * `GET /operationele-taken/:id` — de deadline-meldingen die voor de HUIDIGE
 * deadline van de taak zijn verstuurd (uit de meldingen zelf, niet uit de markers).
 */
export interface TaakDeadlineMeldingenDTO {
  /** "Morgen deadline" aan de uitvoerder. */
  herinnering: TaakDeadlineMeldingDTO | null
  /** "Deadline verstreken" aan de aanmaker (of de escalatie-ontvanger). */
  escalatie: TaakDeadlineMeldingDTO | null
}

/** Hoeveel log-regels en laatste taken het beheerscherm per regel toont. */
export const TAAK_AUTO_REGEL_LOG_MAX = 10

export interface TaakAutoRegelLogDTO {
  id: string
  autoRegelKey: TaakAutoRegelKey
  bronModule: string
  bronEntiteitId: string
  uitkomst: TaakAutoRegelLogUitkomst
  /** "Waarom niet" (of samenvatting), leesbaar. */
  reden: string | null
  /**
   * Bij AANGEMAAKT: de aangemaakte taken. Bij een dedupe-overslag: de taak die
   * er al was (om naartoe te linken). Anders leeg.
   */
  taakIds: string[]
  /** Aantal AANGEMAAKTE taken (0 bij een overslag). */
  aantal: number
  /**
   * Hoe vaak de rij daarna is bijgeschreven (hooguit een keer per etmaal): de
   * etmalen waarin dezelfde uitkomst voor deze bron terugkwam, niet de aanroepen.
   */
  herhaald: number
  actorNaam: string | null
  /** ISO-timestamp van de laatste (herhaalde) aanroep. */
  laatstOp: string
  createdAt: string
}

/** Een van de laatste taken die een regel aanmaakte (beheerscherm). */
export interface TaakAutoRegelTaakDTO {
  id: string
  titel: string
  status: OperationeleTaakStatus
  bronEntiteitId: string | null
  createdAt: string
}

/** GET /operationele-taken/auto-regels — per regel aan/uit + laatste taken + logboek. */
export interface TaakAutoRegelOverzichtDTO {
  key: TaakAutoRegelKey
  actief: boolean
  /** Laatste `TAAK_AUTO_REGEL_LOG_MAX` taken die deze regel aanmaakte (nieuwste eerst). */
  laatsteTaken: TaakAutoRegelTaakDTO[]
  /** Laatste `TAAK_AUTO_REGEL_LOG_MAX` log-regels (nieuwste eerst), inclusief "waarom niet". */
  log: TaakAutoRegelLogDTO[]
  /**
   * Aantal gevallen (bronnen) in de laatste 30 dagen waar GEEN taak kwam en dat
   * aandacht vraagt: de regel stond uit, er is geen set voor dit pad, of het
   * aanmaken mislukte. Ontbrekende taken blijven zo nooit stil.
   */
  overgeslagen30d: number
  /**
   * Aantal gevallen in de laatste 30 dagen waar de taak er al was (dedupe). Dat
   * is de normale gang van zaken en dus geen waarschuwing.
   */
  bestondAl30d: number
}

/**
 * batch-62 taken-ux (FO-464 §2.B.8, review I3) — een taak die de medewerker zelf
 * net gereedmeldde en nog met één klik terug kan zetten ("Toch niet gereed").
 */
/** batch-62 (review N3): hoeveel "net gereedgemeld"-taken het startscherm toont. */
export const RECENT_GEREED_MAX = 20

export interface RecentGereedTaakDTO {
  id: string
  titel: string
  /** ISO-timestamp van het gereedmelden. */
  uitgevoerdOp: string
  /** ISO-timestamp tot wanneer "Toch niet gereed" met één klik kan. */
  ongedaanTot: string
}

/** GET /operationele-taken/mijn/week — meta naast de lijst. */
export interface MijnTakenWeekMetaDTO {
  /** 'yyyy-mm-dd' NL-kalenderdag van vandaag. */
  vandaag: string
  /** 'yyyy-mm-dd' zondag van de lopende ISO-week. */
  weekTot: string
  total: number
  verlopen: number
  vandaagAantal: number
  dezeWeek: number
  zonderDatum: number
  /**
   * batch-62 (review I3): wat de medewerker zelf de afgelopen 24 uur gereedmeldde
   * en nog kan terugzetten (nieuwste eerst, max. `RECENT_GEREED_MAX`). Telt niet
   * mee in `total`.
   */
  recentGereed: RecentGereedTaakDTO[]
  /** batch-62 (review N3): er zijn er meer dan getoond (zichtbaar vangnet, geen stille afkap). */
  recentGereedAfgekapt: boolean
}

/** GET /operationele-taken/mijn/telling — teller-badge (bottom-nav / Meer-tegel). */
export interface MijnTakenTellingDTO {
  /** Toegewezen open taken (GEPLAND + ON_HOLD t/m einde week, plus toegewezen zonder datum). */
  open: number
}

/**
 * batch-62 taken-ux (FO-464 §2.B.10) — `meta` naast de taak na een geslaagde
 * gereedmelding (`POST /:id/uitgevoerd`, en `POST /:id/status` naar Gereed), zodat
 * de UI toont wat er daarna gebeurt zonder iets te beloven wat niet klopt.
 */
export interface TaakGereedmeldMetaDTO {
  /** Niet-blokkerende meldingen, bv. een checklist die nog open stond. */
  waarschuwingen: string[]
  /** Krijgt de aanvrager (aanmaker) een melding? Niet bij een eigen taak of als het event uit staat. */
  aanvragerGeinformeerd: boolean
  /** ISO-timestamp tot wanneer "Toch niet gereed" met één klik kan. */
  ongedaanTot: string | null
}

/** batch-62 taken-ux (FO-464 §5.3) — `error.details` bij een tweede gereedmelding (409). */
export interface TaakAlGereedDetails {
  alGereedgemeld: true
  /** Naam van wie de taak eerder gereedmeldde, of null als onbekend. */
  doorNaam: string | null
}

/** POST /operationele-taken/:id/plannen — toewijzen aan medewerker + datum. */
export const planTaakSchema = z.object({
  uitvoerderId:  idParam('uitvoerderId'),
  geplandeDatum: dagDatum('geplandeDatum'),
  tijdVan:       tijdHHmm('tijdVan').nullable().optional(),
  tijdTot:       tijdHHmm('tijdTot').nullable().optional(),
})
export type PlanTaakInput = z.infer<typeof planTaakSchema>

/** POST /operationele-taken/:id/uitgevoerd — afvinken (bewijs server-side afgedwongen). */
export const uitvoerenSchema = z.object({
  uitvoerOpmerking: z.string().trim().max(5000).nullable().optional(),
  kilometerstand:   z.number().int().min(0).nullable().optional(),
  uitvoerLat:       coordLat.nullable().optional(),
  uitvoerLng:       coordLng.nullable().optional(),
})
export type UitvoerenInput = z.infer<typeof uitvoerenSchema>

/** POST /operationele-taken/:id/niet-uitgevoerd — afmelden met reden. */
export const nietUitgevoerdSchema = z.object({
  reden:     z.enum(NIET_UITGEVOERD_REDEN),
  opmerking: z.string().trim().max(5000).nullable().optional(),
})
export type NietUitgevoerdInput = z.infer<typeof nietUitgevoerdSchema>

/** POST /operationele-taken/:id/prioriteit — handmatige prio-wijziging (planner). */
export const prioriteitSchema = z.object({
  prioriteit: z.enum(TAAK_PRIORITEIT_OP),
})
export type PrioriteitInput = z.infer<typeof prioriteitSchema>

/**
 * Status-filter dat zowel een comma-list ("BACKLOG,GEPLAND") als herhaalde
 * query-params (?status=BACKLOG&status=GEPLAND) als één waarde accepteert,
 * en normaliseert naar een gevalideerde `OperationeleTaakStatus[]`.
 */
const statusFilterSchema = z.preprocess(
  (v) => {
    if (v === undefined || v === null) return undefined
    const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [v]
    const cleaned = raw
      .map((s) => (typeof s === 'string' ? s.trim() : s))
      .filter((s) => s !== '' && s !== undefined)
    return cleaned.length > 0 ? cleaned : undefined
  },
  z.array(z.enum(OPERATIONELE_TAAK_STATUS)).optional(),
)

/**
 * GET /operationele-taken — open-lijst + filters (alles optioneel).
 *
 * Paginering is OPT-IN: zonder `page`/`pageSize` geeft de API — net als vroeger —
 * ALLE gefilterde rijen terug (`meta.page=1`, `meta.pageSize=total`), zodat
 * bestaande consumers ongewijzigd blijven. Met `page`/`pageSize` wordt geslicet.
 */
export const TAAK_DATUM_VELD = ['gepland', 'deadline', 'aangemaakt'] as const
export type TaakDatumVeld = (typeof TAAK_DATUM_VELD)[number]
export const TAAK_DATUM_VELD_LABELS: Record<TaakDatumVeld, string> = {
  gepland:    'Geplande datum',
  deadline:   'Deadline',
  aangemaakt: 'Aangemaakt',
}

export const listOperationeleTakenQuerySchema = z.object({
  status:       statusFilterSchema,
  prioriteit:   prioriteitOpSchema.optional(),
  type:         taakTypeSchema.optional(),
  uitvoerderId: idParam('uitvoerderId').optional(),
  clientId:     idParam('clientId').optional(),
  van:          dagDatum('van').optional(),
  tot:          dagDatum('tot').optional(),
  // batch-61 (FO-447): op welk datumveld `van`/`tot` filtert. Default 'gepland'
  // (bestaand gedrag, planbord + planning-badge); 'deadline' / 'aangemaakt' voor
  // de lijstfilters. `aangemaakt` vergelijkt de createdAt-timestamp op NL-dag.
  datumVeld:    z.enum(TAAK_DATUM_VELD).optional(),
  // batch-61 (FO-447): herkomst-module (exacte match, opties via /filter-opties).
  bronModule:   z.string().trim().min(1).max(100).optional(),
  q:            z.string().trim().max(200).optional(),
  // batch-67 taken-engine (FO-498 §2.5): "mijn team" voor teamleiders. De server
  // leidt het team af uit de ingelogde gebruiker (afdeling + contracten waarop
  // hij teamleider is), nooit uit een meegestuurde lijst.
  mijnTeam:     queryBoolean().optional(),
  page:         z.coerce.number().int().min(1).optional(),
  pageSize:     z.coerce.number().int().min(1).max(200).optional(),
})
export type ListOperationeleTakenQuery = z.infer<typeof listOperationeleTakenQuerySchema>

// ─── Templates ────────────────────────────────────────────────────────────────

/**
 * batch-62 taken-ux (FO-464) — standaard-deadline van een sjabloon in
 * kalenderdagen na aanmaken (0 = vandaag; null = geen standaard-deadline).
 */
const standaardDeadlineDagenSchema = z
  .number()
  .int({ message: 'Vul een heel aantal dagen in' })
  .min(0, { message: 'Het aantal dagen kan niet negatief zijn' })
  .max(365, { message: 'Maximaal 365 dagen' })
  .nullable()
  .optional()

// batch-62 taken-ux (review N2): Nederlandse meldingen, zodat een invoerfout
// inline in het sjabloonformulier leesbaar is (sendValidationError geeft de
// melding van het schema ongewijzigd door).
const sjabloonNaamSchema = z
  .string()
  .trim()
  .min(1, { message: 'Geef het sjabloon een naam.' })
  .max(200, { message: 'Houd de naam kort (maximaal 200 tekens).' })
const sjabloonTitelSchema = z
  .string()
  .trim()
  .max(200, { message: 'Houd de titel kort (maximaal 200 tekens).' })
  .nullable()
  .optional()
const sjabloonVerplichteVeldenSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1, { message: 'Een verplicht veld mag niet leeg zijn.' })
      .max(100, { message: 'Een veldnaam is maximaal 100 tekens.' }),
  )
  .max(50, { message: 'Maximaal 50 verplichte velden.' })
  .nullable()
  .optional()
const sjabloonChecklistSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1, { message: 'Een checklist-punt mag niet leeg zijn.' })
      .max(200, { message: 'Een checklist-punt is maximaal 200 tekens.' }),
  )
  .max(100, { message: 'Een checklist heeft maximaal 100 punten.' })
  .nullable()
  .optional()

/** POST /operationele-taken/templates — nieuw taak-sjabloon. */
export const templateCreateSchema = z.object({
  naam:                       sjabloonNaamSchema,
  type:                       taakTypeSchema.default('OVERIG'),
  standaardTitel:             sjabloonTitelSchema,
  standaardPrioriteit:        prioriteitOpSchema.optional(),
  bewijsEis:                  bewijsEisSchema.optional(),
  verplichteVelden:           sjabloonVerplichteVeldenSchema,
  checklistTemplate:          sjabloonChecklistSchema,
  autoContext:                z.boolean().optional(),
  betreftMedewerkerVerplicht: z.boolean().optional(),
  actief:                     z.boolean().optional(),
  // batch-62 taken-ux (FO-464): standaard-deadline in kalenderdagen na aanmaken.
  standaardDeadlineDagen:     standaardDeadlineDagenSchema,
})
export type TemplateCreateInput = z.infer<typeof templateCreateSchema>

/** PATCH /operationele-taken/templates/:id — sjabloon bijwerken (partial). */
export const templateUpdateSchema = z.object({
  naam:                       sjabloonNaamSchema.optional(),
  type:                       taakTypeSchema.optional(),
  standaardTitel:             sjabloonTitelSchema,
  standaardPrioriteit:        prioriteitOpSchema.optional(),
  bewijsEis:                  bewijsEisSchema.optional(),
  verplichteVelden:           sjabloonVerplichteVeldenSchema,
  checklistTemplate:          sjabloonChecklistSchema,
  autoContext:                z.boolean().optional(),
  betreftMedewerkerVerplicht: z.boolean().optional(),
  actief:                     z.boolean().optional(),
  standaardDeadlineDagen:     standaardDeadlineDagenSchema,
})
export type TemplateUpdateInput = z.infer<typeof templateUpdateSchema>

/** PATCH /operationele-taken/auto-regels/:key — auto-aanmaak-regel aan/uit. */
export const autoRegelUpdateSchema = z.object({
  actief: z.boolean(),
})
export type AutoRegelUpdateInput = z.infer<typeof autoRegelUpdateSchema>

// ─── Import ───────────────────────────────────────────────────────────────────

/** Eén gecorrigeerde import-rij vanuit het controlescherm. */
export const importRijVerwerkSchema = z.object({
  titel:               z.string().trim().min(1).max(200),
  type:                taakTypeSchema.optional(),
  omschrijving:        z.string().trim().max(5000).nullable().optional(),
  prioriteit:          prioriteitOpSchema.optional(),
  categorie:           z.string().trim().max(100).nullable().optional(),
  clientId:            idParam('clientId').nullable().optional(),
  locatieAdres:        z.string().trim().max(500).nullable().optional(),
  uitvoerderId:        idParam('uitvoerderId').nullable().optional(),
  betreftMedewerkerId: idParam('betreftMedewerkerId').nullable().optional(),
  voertuigId:          idParam('voertuigId').nullable().optional(),
  voertuigVrij:        z.string().trim().max(100).nullable().optional(),
  deadline:            dagDatum('deadline').nullable().optional(),
  opmerking:           z.string().trim().max(5000).nullable().optional(),
})
export type ImportRijVerwerkInput = z.infer<typeof importRijVerwerkSchema>

/** POST /operationele-taken/import/:batchId/verwerk — gecorrigeerde rijen wegschrijven. */
export const importVerwerkSchema = z.object({
  rijen: z.array(importRijVerwerkSchema).min(1).max(1000),
})
export type ImportVerwerkInput = z.infer<typeof importVerwerkSchema>

// ─── Categorieën (gedeelde categorie-labels, model DagTaakCategorie) ──────────
//
// De categorie is een visuele tag (gekleurde chip) naast `type` (systeem-
// semantiek). De labels leven in het `DagTaakCategorie`-model (tabel
// `dagtaak_categorieen`) — bewaard sinds de DagTaak-retirement omdat dit de
// categorie-bron is voor `OperationeleTaak.categorie` + de
// `/operationele-taken/categorieen`-endpoints. `categorie` op de taak is een
// string-label-ref (geen harde FK), zodat een taak een verwijderd label kan
// behouden; `DagTaakCategorie.label` is `@unique`, deactiveren via `actief=false`.

export interface DagTaakCategorieDTO {
  id: string
  label: string
  kleur: string | null
  volgorde: number
  actief: boolean
}

/** POST /operationele-taken/categorieen — nieuwe categorie-chip. */
export const dagTaakCategorieCreateSchema = z.object({
  label:    z.string().trim().min(1).max(50),
  kleur:    z.string().trim().max(20).nullable().optional(),
  volgorde: z.number().int().min(0).optional(),
  actief:   z.boolean().optional(),
})
export type DagTaakCategorieCreateInput = z.infer<typeof dagTaakCategorieCreateSchema>

/** PATCH /operationele-taken/categorieen/:id — categorie bijwerken (alles optioneel). */
export const dagTaakCategorieUpdateSchema = z.object({
  label:    z.string().trim().min(1).max(50).optional(),
  kleur:    z.string().trim().max(20).nullable().optional(),
  volgorde: z.number().int().min(0).optional(),
  actief:   z.boolean().optional(),
})
export type DagTaakCategorieUpdateInput = z.infer<typeof dagTaakCategorieUpdateSchema>
